import {I18nContext} from '@logistics-frontend/core/i18n'
import React, {lazy, PropsWithChildren, Suspense} from 'react'

import type {RumSettings} from '../types'
import {getRumGlobal, GLOBAL_OBJ} from '../utils'

enum ErrorBoundaryTemplate {
  splashScreen = 'splashScreen',
  topBanner = 'topBanner',
}

type ErrorBoundaryProps = PropsWithChildren<{
  template?: ErrorBoundaryTemplate
}>

type ErrorBoundaryState = {
  hasError: boolean
}

const ErrorAlert = lazy(() => import('@logistics-frontend/core/ui/alerts'))

const ErrorNotify = lazy(() => import('@logistics-frontend/blocks/error/ErrorNotify'))

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private fallbackComponent?: JSX.Element

  static contextType = I18nContext

  static defaultProps = {
    template: ErrorBoundaryTemplate.splashScreen,
  }

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {hasError: false}
  }

  static getDerivedStateFromError() {
    return {hasError: true}
  }

  componentDidMount() {
    const t = this.context
    const reqId = getRumGlobal()?.getErrorSetting?.('reqid' as keyof RumSettings['reqid'])
    const fallbackComponents: {[key in ErrorBoundaryTemplate]: JSX.Element} = {
      topBanner: <Suspense children={<ErrorAlert />} />,
      splashScreen: <Suspense children={<ErrorNotify {...{reqId, t}} />} />,
    }

    this.fallbackComponent = fallbackComponents[this.props.template!]
  }

  componentDidCatch(error: Ya.Rum.LoggableError, additional: React.ErrorInfo) {
    const {logError} = getRumGlobal() || {}
    const commonArgs: [Ya.Rum.LogErrorOptions, Ya.Rum.LoggableError] = [
      {additional, type: 'error-boundary'},
      error || new Error('MISSING_ERROR'),
    ]
    const basePath = GLOBAL_OBJ?.location?.pathname?.split('/')[1]!

    logError?.(...commonArgs)

    if (['account2', 'order'].includes(basePath)) {
      logError?.(...commonArgs, {project: `logistics-frontend-${basePath}`})
    }
  }

  render() {
    return this.state.hasError ? this.fallbackComponent : this.props.children
  }
}
